import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AnimeDetailDTO, AnimeListItemDTO, CharacterDetailDTO, Page } from '@anidex/shared/types';
import { CARD_PAGE_SIZE, pageCursor } from '../../hooks/useListPage';
import { api, queryString } from '../api';
import { hasListIntent } from '../list-intent';
import type { AnimeCharacterSort, AnimeFilters } from './filters';
import { keys } from './keys';

export function useAnimes(filters: AnimeFilters, enabled = true, keepPrevious = false) {
  const page = filters.page ?? 0;
  const limit = filters.limit ?? CARD_PAGE_SIZE;
  const intent = hasListIntent(filters) || Boolean(filters.sort && filters.sort !== 'popularity');
  return useQuery({
    queryKey: keys.animes(filters),
    queryFn: () =>
      api<Page<AnimeListItemDTO>>(
        `/animes${queryString({
          search: filters.search,
          rank: filters.rank,
          filter: filters.filter,
          sort: filters.sort,
          limit,
          cursor: pageCursor(page, limit),
        })}`,
      ),
    placeholderData: keepPrevious && intent ? keepPreviousData : undefined,
    enabled: enabled && intent,
  });
}

export function useAnime(id: number, show: 'all' | 'owned' | 'missing', page = 0, sort: AnimeCharacterSort = 'rarity') {
  const limit = CARD_PAGE_SIZE;
  return useQuery({
    queryKey: keys.anime(id, show, page, sort),
    queryFn: () =>
      api<AnimeDetailDTO>(
        `/animes/${id}${queryString({ show, sort: sort === 'rarity' ? undefined : sort, cursor: pageCursor(page, limit), limit })}`,
      ),
    placeholderData: keepPreviousData,
  });
}

export function usePublicCharacter(id: number | null) {
  return useQuery({
    queryKey: keys.publicCharacter(id ?? 0),
    queryFn: () => api<{ character: CharacterDetailDTO['character'] }>(`/seo/characters/${id}`),
    enabled: id !== null,
  });
}

export function usePublicAnime(id: number | null) {
  return useQuery({
    queryKey: keys.publicAnime(id ?? 0),
    queryFn: () =>
      api<{
        anime: AnimeDetailDTO['anime'];
        characters: Array<{ character: CharacterDetailDTO['character']; ownership: null }>;
      }>(`/seo/animes/${id}`),
    enabled: id !== null,
  });
}
